import { Inject, Injectable } from '@angular/core';
import { Device } from '@ionic-native/device/ngx';
import { Observable, Subject } from 'rxjs';
import { DeviceWsConnector } from './device-ws.connector';
import {
  POS_COMMAND_HUB_METHOD,
  POS_COMMAND_REQUEST_HUB_METHOD,
  PosCommandEnvelope,
  PosCommandHandler,
  PosCommandName,
  PosCommandResult,
} from './pos-command.models';

// Commands from the admin, through Pos.Ws. Each command name has one handler; what it returns
// (or the error it throws) is the result the admin sees. Features add their commands with register().
@Injectable({ providedIn: 'root' })
export class PosTerminalCommunicationService {
  private readonly _syncDataSubject = new Subject<void>();
  private readonly _prroConfigUpdateSubject = new Subject<void>();
  private readonly _handlers = new Map<string, PosCommandHandler>();
  // Results of recent commands by id: a repeated delivery gets the same result, the command does not run twice.
  private readonly _recentResults = new Map<string, Promise<PosCommandResult>>();
  private readonly _recentResultsLimit = 100;

  readonly onSyncData: Observable<void> = this._syncDataSubject.asObservable();
  readonly onPrroConfigUpdate: Observable<void> =
    this._prroConfigUpdateSubject.asObservable();

  constructor(
    private _deviceWsConnector: DeviceWsConnector,
    private _device: Device,
    @Inject('ENVIRONMENT') private _environment: { version?: string },
  ) {
    this._registerBuiltInCommands();
    this._listenCommands();
  }

  // Adds a command handler; returns a function that removes it.
  register(command: PosCommandName | string, handler: PosCommandHandler): () => void {
    this._handlers.set(command, handler);
    return () => {
      if (this._handlers.get(command) === handler) {
        this._handlers.delete(command);
      }
    };
  }

  private _registerBuiltInCommands() {
    this.register('syncData', () => {
      this._syncDataSubject.next();
      return { accepted: true };
    });
    this.register('prroConfigUpdate', () => {
      this._prroConfigUpdateSubject.next();
      return { accepted: true };
    });
    // Round trip check: admin -> Erp.Ws -> RabbitMQ -> Pos.Ws -> terminal and back.
    this.register('ping', (payload) => ({
      pong: true,
      deviceId: this._device.uuid,
      platform: this._device.platform,
      appVersion: this._environment?.version,
      url: location.pathname,
      receivedAtUtc: new Date().toISOString(),
      echo: payload ?? null,
    }));
  }

  private _listenCommands() {
    this._deviceWsConnector.handle(POS_COMMAND_REQUEST_HUB_METHOD, (data) =>
      this._execute(data),
    );
    // A Pos.Ws without results still sends the old fire-and-forget method.
    this._deviceWsConnector
      .on(POS_COMMAND_HUB_METHOD)
      .subscribe((data) => void this._execute(data));
    // No connect() here: AppComponent connects after platform.ready(), before that a native
    // device has no uuid yet and would connect under a fallback id.
  }

  private _execute(data: unknown): Promise<PosCommandResult> {
    const envelope = this._normalizeEnvelope(data);
    if (!envelope) {
      return Promise.resolve({ success: false, error: 'Invalid command envelope' });
    }

    if (envelope.deviceId !== this._device.uuid) {
      return Promise.resolve({
        success: false,
        error: `Command for device ${envelope.deviceId}, this device is ${this._device.uuid}`,
      });
    }

    const id = envelope.correlationId;
    const known = id ? this._recentResults.get(id) : undefined;
    if (known) {
      return known;
    }

    const result = this._run(envelope);
    if (id) {
      this._remember(id, result);
    }

    return result;
  }

  private async _run(envelope: PosCommandEnvelope): Promise<PosCommandResult> {
    const handler = this._handlers.get(envelope.command);
    if (!handler) {
      return { success: false, error: `Unknown command ${envelope.command}` };
    }

    try {
      const data = await handler(envelope.payload, envelope);
      // undefined is not JSON: the result must always carry a value.
      return { success: true, data: data === undefined ? null : data };
    } catch (err) {
      console.error(`Command ${envelope.command} (${envelope.correlationId}) failed`, err);
      return { success: false, error: this._errorMessage(err) };
    }
  }

  private _remember(id: string, result: Promise<PosCommandResult>) {
    this._recentResults.set(id, result);
    while (this._recentResults.size > this._recentResultsLimit) {
      const oldest = this._recentResults.keys().next().value;
      this._recentResults.delete(oldest);
    }
  }

  private _normalizeEnvelope(data: unknown): PosCommandEnvelope | null {
    if (!data || typeof data !== 'object') {
      return null;
    }

    const envelope = data as PosCommandEnvelope;
    if (!envelope.command || !envelope.deviceId) {
      return null;
    }

    return envelope;
  }

  private _errorMessage(err: unknown): string {
    if (typeof err === 'string') {
      return err;
    }

    return (err as Error)?.message || String(err) || 'Command failed';
  }
}
